/* FAQ artwork: a reliable, opacity-only rotation that rests outside the viewport. */
(() => {
  const ROTATION_DELAY = 6000;
  if (customElements.get('cgau-faq-art')) return;
  customElements.define('cgau-faq-art', class extends HTMLElement {
    connectedCallback() {
      this.images = [...this.querySelectorAll('img')];
      if (this.images.length !== 2) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.visible = false;
      this.ready = false;
      this.motion.addEventListener('change', () => this.updateMotion(), options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      window.addEventListener('pagehide', () => clearTimeout(this.timer), options);
      window.addEventListener('pageshow', () => this.schedule(), options);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= .25;
        if (entries[0].isIntersecting) this.loadImages();
        this.schedule();
      }, { threshold: [0, .25] });
      this.observer.observe(this);
      this.updateMotion();
    }
    async loadImages() {
      if (this.loadingImages) return;
      this.loadingImages = true;
      this.loadAttempt = (this.loadAttempt || 0) + 1;
      this.images.forEach(image => {
        if (image.dataset.src) { image.src = image.dataset.src; delete image.dataset.src; }
        else if (this.loadAttempt > 1 && image.complete && image.naturalWidth === 0 && image.src) {
          const retryUrl = new URL(image.currentSrc || image.src, document.baseURI);
          retryUrl.searchParams.set('cgau-faq-retry', String(this.loadAttempt));
          image.loading = 'eager';
          image.src = retryUrl.href;
        }
      });
      await Promise.all(this.images.map(image => new Promise(resolve => {
        if (image.complete && image.naturalWidth > 0) { resolve(); return; }
        const settle = () => {
          image.removeEventListener('load', settle);
          image.removeEventListener('error', settle);
          resolve();
        };
        image.addEventListener('load', settle, { once: true });
        image.addEventListener('error', settle, { once: true });
        if (image.complete) queueMicrotask(settle);
      })));
      await Promise.all(this.images.map(image => image.decode ? image.decode().catch(() => {}) : Promise.resolve()));
      this.loadingImages = false;
      if (!this.isConnected) return;
      this.ready = this.images.every(image => image.naturalWidth > 0);
      if (!this.ready && this.visible) {
        clearTimeout(this.retryTimer);
        const retryDelay = Math.min(1000 * 2 ** Math.min(this.loadAttempt - 1, 4), 16000);
        this.retryTimer = setTimeout(() => this.loadImages(), retryDelay);
      }
      this.schedule();
    }
    updateMotion() {
      this.setAttribute('role', 'group');
      this.removeAttribute('tabindex');
      this.removeAttribute('aria-pressed');
      this.setAttribute('aria-label', 'CamGuard customer support artwork');
      this.images.forEach((image, index) => image.setAttribute('aria-hidden', String(!this.motion.matches && index !== this.index)));
      this.schedule();
    }
    schedule() {
      clearTimeout(this.timer);
      if (!this.isConnected || !this.ready || !this.visible || document.hidden || this.motion.matches) return;
      this.timer = setTimeout(() => {
        if (!this.isConnected || !this.ready || !this.visible || document.hidden || this.motion.matches) return;
        this.index = 1 - this.index;
        this.dataset.active = String(this.index);
        this.images.forEach((image, index) => image.setAttribute('aria-hidden', String(index !== this.index)));
        this.schedule();
      }, ROTATION_DELAY);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.retryTimer);
      this.observer?.disconnect();
      this.controller?.abort();
      this.loadingImages = false;
    }
  });
})();
